import axios, { AxiosError, AxiosInstance } from "axios";
import { API_CONFIG } from "../config/endpoints";
import { CheckoutIntent, PaymentHistory, PaymentOrder, ScanData, ScanResult, SessionInfo, Usage } from "../types";

const SESSION_STORAGE_KEY = "financify_hs_session";
const DEVICE_ID_STORAGE_KEY = "financify_hs_device_id";

export function readSession(): SessionInfo | null {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.email && parsed.token) return parsed;
    return null;
  } catch {
    return null;
  }
}

export function writeSession(session: SessionInfo | null) {
  if (session) localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  else localStorage.removeItem(SESSION_STORAGE_KEY);
}

// A soft per-device identifier for the free-tier scan cap - see
// pricing_model.py's FREE_MONTHLY_SCANS. Deliberately NOT cleared by
// clearScan()/logout (unlike the session above): the whole point is that it
// survives a sign-out and stays the same across whichever email logs in next
// on this browser, so financify-saas can cap free scans per device across
// accounts, not just per email. This raises the cost of cycling through
// throwaway emails for free scans without requiring phone verification or
// blocking shared devices/networks outright - a user who clears storage or
// switches browsers isn't caught, which is an accepted tradeoff, not a bug.
export function getDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (!id) {
      id = typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(DEVICE_ID_STORAGE_KEY, id);
    }
    return id;
  } catch {
    // localStorage unavailable (private-browsing edge cases, etc.) - fall
    // back to a per-load id rather than throwing; it just won't persist,
    // so this request looks like a fresh device every time, same as today.
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function attachHeaders(client: AxiosInstance) {
  client.interceptors.request.use((config) => {
    const session = readSession();
    if (session) {
      config.headers["X-Financify-Email"] = session.email;
      config.headers["X-Financify-Session-Token"] = session.token;
    }
    config.headers["X-Honey-Device-Id"] = getDeviceId();
    return config;
  });
  return client;
}

export const honeyApi = attachHeaders(
  axios.create({ baseURL: API_CONFIG.honeyApiBase, timeout: 120000 })
);

export const authApi = attachHeaders(
  axios.create({ baseURL: API_CONFIG.authApiBase, timeout: 120000 })
);

/** Extract a human-friendly error message from any axios failure. */
export function errorMessage(err: unknown, fallback = "Something went wrong. Please try again."): string {
  const ax = err as AxiosError<any>;
  if (ax?.response?.data) {
    const detail = (ax.response.data as any).detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (Array.isArray(detail) && detail.length) {
      const first = detail[0];
      if (first?.msg) return String(first.msg);
    }
    if (typeof ax.response.data === "string" && ax.response.data.length < 300) return ax.response.data;
  }
  if (ax?.code === "ERR_NETWORK") return "Could not reach the server. Check your connection and try again.";
  if (ax?.message && ax.message.length < 200) return ax.message;
  return fallback;
}

// ---------------------------------------------------------------------------
// Auth / usage (Financify SaaS)
// ---------------------------------------------------------------------------
export async function requestOtp(email: string): Promise<{ ok: boolean; message?: string; dev_note?: string }> {
  const res = await authApi.post(API_CONFIG.auth.requestOtp, { email });
  return res.data;
}

export async function verifyOtp(email: string, otp: string): Promise<{ email: string; session_token: string; usage?: Usage }> {
  const res = await authApi.post(API_CONFIG.auth.verifyOtp, { email, otp });
  const data = res.data || {};
  return {
    email: data.email || email,
    session_token: data.session_token || data.token || data.sessionToken,
    usage: data.usage,
  };
}

export async function logoutSession(email: string, token: string): Promise<void> {
  await authApi.post(API_CONFIG.auth.logout, { email, session_token: token });
}

export async function logoutAllSessions(email: string, token: string): Promise<void> {
  await authApi.post(API_CONFIG.auth.logoutAll, { email, session_token: token });
}

export async function checkUsage(email: string, token: string): Promise<Usage> {
  const res = await authApi.post(API_CONFIG.auth.usageCheck, {
    email,
    tool_name: API_CONFIG.toolName,
    session_token: token,
  });
  return res.data;
}

export async function recordUsage(email: string, token: string): Promise<Usage> {
  const res = await authApi.post(API_CONFIG.auth.usageRecord, {
    email,
    tool_name: API_CONFIG.toolName,
    session_token: token,
  });
  return res.data;
}

export async function acceptPolicy(email: string, token: string, checkoutReference = "scan_pack_checkout"): Promise<{ ok: boolean }> {
  const res = await authApi.post(API_CONFIG.auth.policyAccept, {
    email,
    session_token: token,
    policy_name: "scan_pack_purchase_terms",
    policy_version: "2026-07-13",
    checkout_reference: checkoutReference,
  });
  return res.data;
}

export async function createPaymentIntent(
  email: string,
  token: string,
  plan: "starter" | "standard" | "pro" | "extra",
  quantity = 1
): Promise<CheckoutIntent> {
  const res = await authApi.post(API_CONFIG.auth.checkoutIntents, {
    email,
    session_token: token,
    plan,
    quantity,
  });
  return res.data;
}

export async function getPaymentStatus(intentId: string): Promise<PaymentOrder> {
  const res = await authApi.get(API_CONFIG.auth.paymentStatus(intentId));
  return res.data;
}

export async function getPaymentHistory(limit = 100): Promise<PaymentHistory> {
  const res = await authApi.get(API_CONFIG.auth.paymentHistory, { params: { limit } });
  return res.data;
}

export async function getAdminDashboard(): Promise<Record<string, any>> {
  const res = await authApi.get(API_CONFIG.auth.adminDashboard);
  return res.data;
}

// ---------------------------------------------------------------------------
// Decision Journal (persistent Conviction Checklist history) + public Track
// Record (verdict-vs-forward-return ledger).
// ---------------------------------------------------------------------------
export interface ChecklistJournalEntry {
  id?: string;
  email?: string;
  ticker: string;
  company_name?: string | null;
  answers: Record<string, { answer?: string | null; notes?: string | null }>;
  score?: number | null;
  verdict?: string | null;
  financify_score?: number | null;
  entry_date: string;
  updated_at?: string;
}

export async function saveChecklistJournalEntry(payload: {
  ticker: string;
  company_name?: string | null;
  answers: Record<string, { answer?: string | null; notes?: string | null }>;
  score?: number | null;
  verdict?: string | null;
  financify_score?: number | null;
}): Promise<ChecklistJournalEntry> {
  const res = await authApi.post(API_CONFIG.auth.journalChecklist, payload);
  return res.data;
}

export async function fetchChecklistJournalTickers(): Promise<{ tickers: ChecklistJournalEntry[] }> {
  const res = await authApi.get(API_CONFIG.auth.journalChecklist);
  return res.data;
}

export async function fetchChecklistJournalHistory(ticker: string): Promise<{ ticker: string; history: ChecklistJournalEntry[] }> {
  const res = await authApi.get(API_CONFIG.auth.journalChecklist, { params: { ticker } });
  return res.data;
}

export async function deleteChecklistJournalEntry(entryId: string): Promise<void> {
  await authApi.delete(API_CONFIG.auth.journalChecklistEntry(entryId));
}

// ---------------------------------------------------------------------------
// Watchlist (persistent per-user saved scan snapshots - see WatchlistTab).
// ---------------------------------------------------------------------------
export interface WatchlistSnapshotRecord {
  id: string;
  ticker: string;
  company_name?: string | null;
  saved_at: string;
  score: number | null;
  classification?: string | null;
  investor_mode?: string | null;
  filters: { Filter: string; Result: string; "Latest Value": string }[];
}

export async function saveWatchlistSnapshot(payload: {
  ticker: string;
  company_name?: string | null;
  saved_at?: string;
  score?: number | null;
  classification?: string | null;
  investor_mode?: string | null;
  filters: { Filter: string; Result: string; "Latest Value": string }[];
}): Promise<WatchlistSnapshotRecord> {
  const res = await authApi.post(API_CONFIG.auth.watchlistSnapshot, payload);
  return res.data;
}

export async function fetchWatchlistSnapshots(): Promise<{ items: WatchlistSnapshotRecord[] }> {
  const res = await authApi.get(API_CONFIG.auth.watchlistSnapshot);
  return res.data;
}

export async function deleteWatchlistSnapshot(snapshotId: string): Promise<void> {
  await authApi.delete(API_CONFIG.auth.watchlistSnapshotById(snapshotId));
}

export async function clearWatchlist(): Promise<void> {
  await authApi.delete(API_CONFIG.auth.watchlistSnapshot);
}

export interface TrackRecordHorizonStat {
  count: number;
  avg_stock_return: number | null;
  avg_nifty_return: number | null;
}

export type TrackRecordHorizonBuckets = Record<string, { "1m": TrackRecordHorizonStat; "3m": TrackRecordHorizonStat; "6m": TrackRecordHorizonStat; "1y": TrackRecordHorizonStat }>;

export interface TrackRecordSummary {
  by_verdict: TrackRecordHorizonBuckets;
  // Non-advisory Scan Tracker categories (quality/valuation are descriptive
  // facts about the business/price, never Buy/Sell/Hold/Avoid instructions).
  // Older rows logged before these columns existed fall under "Unknown".
  by_combined_category?: TrackRecordHorizonBuckets;
  by_quality_tier?: TrackRecordHorizonBuckets;
  by_valuation_tier?: TrackRecordHorizonBuckets;
  total_logged: number;
  unique_tickers: number;
  tracking_since: string | null;
}

export async function fetchTrackRecordSummary(): Promise<TrackRecordSummary> {
  const res = await authApi.get(API_CONFIG.auth.trackRecordSummary);
  return res.data;
}

export interface PersonalTrackRecordSummary {
  by_verdict: TrackRecordHorizonBuckets;
  total_logged: number;
  unique_tickers: number;
  tracking_since: string | null;
}

export async function fetchMyTrackRecordSummary(): Promise<PersonalTrackRecordSummary> {
  const res = await authApi.get(API_CONFIG.auth.myTrackRecordSummary);
  return res.data;
}

export interface ScanStreak {
  current_streak: number;
  longest_streak: number;
  last_scan_date: string | null;
}

export async function fetchScanStreak(): Promise<ScanStreak> {
  const res = await authApi.get(API_CONFIG.auth.streak);
  return res.data;
}

// ---------------------------------------------------------------------------
// Tenet API (fastapi_app.py contract)
// ---------------------------------------------------------------------------
export async function apiHealth(): Promise<any> {
  const res = await honeyApi.get(API_CONFIG.honey.health);
  return res.data;
}

function requestId(prefix: string) {
  const random = typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${random}`;
}

export async function scanTicker(ticker: string, includePdf = false, scanType = "normal_scan"): Promise<ScanResult> {
  const res = await honeyApi.get(API_CONFIG.honey.scanTicker(ticker), {
    params: { include_pdf: includePdf },
    headers: { "X-Honey-Scan-Type": scanType, "X-Honey-Request-Id": requestId(scanType) },
  });
  return res.data;
}

export async function scanClean(data: ScanData, includePdf = false, scanType = "normal_scan"): Promise<ScanResult> {
  const res = await honeyApi.post(API_CONFIG.honey.scan, data, {
    params: { include_pdf: includePdf },
    headers: { "X-Honey-Scan-Type": scanType, "X-Honey-Request-Id": requestId(scanType) },
  });
  return res.data;
}

export async function scanUpload(file: File, includePdf = false): Promise<ScanResult> {
  const form = new FormData();
  form.append("file", file);
  const res = await honeyApi.post(API_CONFIG.honey.scanUpload, form, {
    params: { include_pdf: includePdf },
    headers: { "Content-Type": "multipart/form-data", "X-Honey-Scan-Type": "normal_scan", "X-Honey-Request-Id": requestId("upload") },
  });
  return res.data;
}

export async function recordPeerComparison(): Promise<Usage | null> {
  const res = await honeyApi.post(API_CONFIG.honey.peerUsage, {}, {
    headers: { "X-Honey-Scan-Type": "peer_comparison", "X-Honey-Request-Id": requestId("peer") },
  });
  return res.data?.usage || null;
}

export interface ProfessorBeeAnswer {
  answer: string;
  remaining: number | null;
  limit: number | null;
  global_remaining: number | null;
}

export async function askProfessorBee(payload: {
  question: string;
  company?: string;
  ticker?: string;
  financify_score?: number;
  context_snippet?: string;
}): Promise<ProfessorBeeAnswer> {
  const res = await honeyApi.post(API_CONFIG.honey.professorBeeAsk, payload, {
    headers: { "X-Honey-Scan-Type": "no_charge", "X-Honey-Request-Id": requestId("professor-bee") },
  });
  return res.data;
}

export type ChecklistVerdict = "PASS" | "PARTIAL" | "FAIL" | "UNKNOWN";

export interface ChecklistBatchVerdict {
  id: number;
  verdict: ChecklistVerdict;
  reasoning: string;
}

export interface ProfessorBeeChecklistBatchAnswer {
  verdicts: ChecklistBatchVerdict[];
  remaining: number | null;
  limit: number | null;
  global_remaining: number | null;
}

// Conviction Checklist tab's "Auto-fill with Themis" button - answers every
// question given in ONE call (mode "checklist_verdict_batch" on the same
// backend endpoint), instead of a separate call per question. Uses its own,
// separate daily/monthly quota from the general chat widget/askProfessorBee
// above - see server.py's _professor_bee_checklist_batch.
export async function askProfessorBeeChecklistBatch(payload: {
  company?: string;
  ticker?: string;
  financify_score?: number;
  context_snippet?: string;
  questions: { id: number; question: string; red_flags: string }[];
}): Promise<ProfessorBeeChecklistBatchAnswer> {
  const res = await honeyApi.post(
    API_CONFIG.honey.professorBeeAsk,
    // `question` is required by the backend's ProfessorBeeRequest model but
    // unused in batch mode - a fixed placeholder keeps that model simple
    // rather than making the field conditionally optional.
    { ...payload, question: "(batch checklist auto-fill)", mode: "checklist_verdict_batch" },
    { headers: { "X-Honey-Scan-Type": "no_charge", "X-Honey-Request-Id": requestId("professor-bee-batch") } },
  );
  return res.data;
}

export async function manualApply(
  data: ScanData,
  manualRows: Record<string, any>[],
  mode: string,
  chargeScan = true
): Promise<{ data: ScanData; scan: ScanResult }> {
  const res = await honeyApi.post(API_CONFIG.honey.manualApply, {
    data,
    manual_rows: manualRows,
    mode,
    charge_scan: chargeScan,
  }, {
    headers: { "X-Honey-Scan-Type": chargeScan ? "manual_scan" : "no_charge", "X-Honey-Request-Id": requestId("manual") },
  });
  return res.data;
}

export async function fetchReportPdf(data: ScanData | ScanResult, premium = false): Promise<Blob> {
  const res = await honeyApi.post(API_CONFIG.honey.reportPdf, data, {
    params: premium ? { premium: true } : undefined,
    responseType: "blob",
    headers: { "X-Honey-Scan-Type": "pdf_download", "X-Honey-Request-Id": requestId("pdf") },
  });
  return res.data;
}

export interface ConvictionChecklistExportPayload {
  company: string;
  ticker: string;
  financify_score?: string | null;
  signal?: string | null;
  roe?: string | null;
  roce?: string | null;
  debt_to_equity?: string | null;
  valuation_verdict?: string | null;
  answers: Record<string, { answer: string | null; notes?: string | null }>;
}

export async function fetchConvictionChecklistPdf(payload: ConvictionChecklistExportPayload): Promise<Blob> {
  const res = await honeyApi.post(API_CONFIG.honey.convictionChecklistPdf, payload, {
    responseType: "blob",
    headers: { "X-Honey-Scan-Type": "pdf_download", "X-Honey-Request-Id": requestId("pdf") },
  });
  return res.data;
}

export async function fetchManualTemplate(): Promise<string> {
  const res = await honeyApi.get(API_CONFIG.honey.manualTemplate, { responseType: "text" });
  return res.data;
}
